// PlayableThumbnail — wrapper de YoutubeThumbnail que agrega affordance
// de play: click para abrir el modal, overlay hover con icon Play,
// focus ring accesible. Sin videoUrl o sin onClick, degrada a la
// thumbnail estática (comportamiento del YoutubeThumbnail original).
//
// Usado en BibliotecaEjercicios + PrescribirRutina para reproducir el
// video del ejercicio desde el catálogo (feature del hallazgo #P3).

import { PlayCircle } from "lucide-react";
import { YoutubeThumbnail } from "./YoutubeThumbnail";

interface Props {
  /** URL del video tutorial de YouTube. undefined = sin video → sin
   *  affordance de play, thumbnail estática con placeholder. */
  videoUrl: string | undefined;
  /** Para el `alt` accesible y el `aria-label` del botón. */
  exerciseName: string;
  /** Callback al click. Requerido para que el thumbnail sea
   *  clickeable. Sin onClick el thumbnail es puramente decorativo. */
  onClick?: () => void;
  /** Override del shape del thumbnail (aspect ratio, margin). Se
   *  forwardea a YoutubeThumbnail. Default aspect-video. */
  className?: string;
}

export function PlayableThumbnail({
  videoUrl,
  exerciseName,
  onClick,
  className,
}: Props) {
  const playable = !!videoUrl && !!onClick;

  if (!playable) {
    return (
      <YoutubeThumbnail
        videoUrl={videoUrl}
        exerciseName={exerciseName}
        className={className}
      />
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`Reproducir video: ${exerciseName}`}
      className="group relative w-full block rounded-lg overflow-hidden focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
    >
      <YoutubeThumbnail
        videoUrl={videoUrl}
        exerciseName={exerciseName}
        className={className}
      />
      {/* Overlay Play — visible hover/focus, translúcido en idle para
          que el thumbnail no quede tapado pero la affordance sea clara. */}
      <div className="absolute inset-0 bg-black/10 group-hover:bg-black/40 group-focus-visible:bg-black/40 transition-colors flex items-center justify-center">
        <div className="rounded-full bg-white/85 group-hover:bg-white group-focus-visible:bg-white p-2.5 shadow-lg transition-all group-hover:scale-105 group-focus-visible:scale-105">
          <PlayCircle
            className="w-6 h-6 text-primary"
            strokeWidth={1.75}
          />
        </div>
      </div>
    </button>
  );
}
